import ConditionalToolTip from "components/design/conditionalToolTip";
import { Button } from "components/DesignSystem/Button/Button";
import Loader from "components/DesignSystem/Loader/loader";
import classNames from "classnames";
import {
  inputFieldVariants,
  wrapperVariants,
} from "components/DesignSystem/Input/BareInput";
import { InputProps } from "components/DesignSystem/TextInput/TextInput";
import { MinusCircle } from "components/icons/MinusCircle";
import { PlusCircle } from "components/icons/PlusCircle";
import { useToast } from "hooks/useToast";
import { forwardRef } from "react";
import { useUpdateCartProductQuantityMutation } from "store/apis/products";
import { BackendError } from "types/utils/error";
import { debounce } from "utils/debouncing";

type CartItemQuantityUpdateProps = {
  cartItemId: string;
  quantity: number;
  entityId: string;
};

const CustomBareInput = forwardRef(
  (
    {
      type = "text",
      customSize = "regular",
      label,
      customType,
      block,
      tooltipText,
      className,
      required = false,
      ...props
    }: InputProps,
    ref: React.Ref<HTMLInputElement>
  ) => (
    <>
      <input
        ref={ref}
        id={props.name}
        type={type}
        className={`${classNames(
          wrapperVariants({
            size: customSize,
            variant: customType ?? "default",
          }),
          inputFieldVariants({ size: customSize })
        )}
          !t-text-center`}
        required={required}
        autoComplete="off"
        {...props}
      />
    </>
  )
);

export const CartItemQuantityUpdate = ({
  cartItemId,
  quantity,
  entityId,
}: CartItemQuantityUpdateProps) => {
  const [updateItemQuantity, { isLoading }] =
    useUpdateCartProductQuantityMutation();
  const { alertToast, successToast } = useToast();

  const handleQuantityUpdate = async (quantity: number) => {
    try {
      await updateItemQuantity({
        entityId,
        cartItemId,
        quantity,
      }).unwrap();
      successToast({ message: "Item quantity updated successfully" });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const handleInputChange = debounce((value: number) => {
    if (value !== quantity) {
      handleQuantityUpdate(value);
    }
  });

  if (isLoading) {
    return (
      <div className="t-w-full t-flex t-justify-center">
        <Loader size="small" />
      </div>
    );
  }

  return (
    <div className="t-flex t-items-center">
      <ConditionalToolTip condition={quantity <= 1 && "Minimum quantity is 1"}>
        <span>
          <Button
            size="small"
            customType="ghost_icon"
            onClick={() => handleQuantityUpdate(Number(quantity) - 1)}
            type="button"
            disabled={quantity <= 1 || isLoading}
          >
            <span className="t-text-text-30">
              <MinusCircle color="currentColor" />
            </span>
          </Button>
        </span>
      </ConditionalToolTip>
      <div className="t-w-10">
        <CustomBareInput
          customSize="small"
          defaultValue={quantity}
          onChange={(e) => handleInputChange(Number(e.target.value))}
        />
      </div>
      <Button
        size="small"
        customType="ghost_icon"
        onClick={() => handleQuantityUpdate(Number(quantity) + 1)}
        type="button"
        disabled={isLoading}
      >
        <span className="t-text-text-30">
          <PlusCircle />
        </span>
      </Button>
    </div>
  );
};
